import type { AdminGame, CreateGameRequest } from '@mcgames/contracts';
import { Button, Spinner, TextInput } from '@mcgames/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';
import { useApiMutation, useApiQuery } from '../../api/hooks.ts';
import type { DeletedGameState } from './DeleteGameControl.tsx';
import { ErrorText } from './ErrorText.tsx';
import { GameStatusBadges } from './GameStatusBadges.tsx';
import { GAMES_PATH, gamePath } from './game-hooks.ts';
import styles from './Games.module.css';

export function GamesPage() {
  const { t } = useTranslation('admin');
  const games = useApiQuery<AdminGame[]>(GAMES_PATH);
  const deletedGame = deletedGameTitle(useLocation().state);

  return (
    <div className={styles['page']}>
      <h1 className={styles['heading']}>{t('games.title')}</h1>
      {deletedGame !== null && (
        <p role="status" className={styles['status']}>
          {t('games.delete.success', { title: deletedGame })}
        </p>
      )}
      <NewGameForm />
      {games.isPending && <Spinner />}
      {games.isError && (
        <p role="alert" className={styles['error']}>
          {t('games.list.loadFailed')}
        </p>
      )}
      {games.data?.length === 0 && <p className={styles['muted']}>{t('games.list.empty')}</p>}
      {games.data !== undefined && games.data.length > 0 && <GamesTable games={games.data} />}
    </div>
  );
}

/** The title the editor passes along after deleting a game; anything else in the state is ignored. */
function deletedGameTitle(state: unknown): string | null {
  const title = (state as Partial<DeletedGameState> | null)?.deletedGame;
  return typeof title === 'string' ? title : null;
}

function NewGameForm() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const create = useApiMutation<CreateGameRequest, AdminGame>('POST', GAMES_PATH, {
    onSuccess: (game) => {
      queryClient.setQueryData<AdminGame>([gamePath(game.id)], game);
      void queryClient.invalidateQueries({ queryKey: [GAMES_PATH] });
      // Artwork is the next step for a new game: identifying it on SteamGridDB fills all images.
      void navigate(`/admin/games/${encodeURIComponent(game.id)}?tab=artwork`);
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (title.trim() !== '') create.mutate({ title: title.trim() });
  }

  return (
    <form onSubmit={submit}>
      <div className={styles['row']}>
        <div className={styles['grow']}>
          <TextInput
            label={t('games.newGame.label')}
            value={title}
            maxLength={200}
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>
        <Button type="submit" disabled={title.trim() === '' || create.isPending}>
          {t('games.newGame.submit')}
        </Button>
      </div>
      <ErrorText error={create.error} />
    </form>
  );
}

function GamesTable({ games }: { games: AdminGame[] }) {
  const { t } = useTranslation('admin');
  return (
    <div className={styles['tableWrap']}>
      <table className={styles['table']} aria-label={t('games.list.label')}>
        <thead>
          <tr>
            <th scope="col" className={styles['coverCell']}>
              {t('games.list.cover')}
            </th>
            <th scope="col">{t('games.list.title')}</th>
            <th scope="col">{t('games.list.versions')}</th>
            <th scope="col">{t('games.list.status')}</th>
          </tr>
        </thead>
        <tbody>
          {games.map((game) => (
            <tr key={game.id}>
              <td className={styles['coverCell']}>
                <GameThumb url={game.coverUrl} />
              </td>
              <td>
                <Link
                  to={`/admin/games/${encodeURIComponent(game.id)}`}
                  className={styles['titleLink']}
                >
                  {game.title}
                </Link>
              </td>
              <td>{game.versions.length}</td>
              <td>
                <GameStatusBadges game={game} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GameThumb({ url }: { url: string | null }) {
  return url !== null ? (
    <img className={styles['thumb']} src={url} alt="" loading="lazy" />
  ) : (
    <div className={styles['thumb']} aria-hidden="true" />
  );
}
